import { TypedDocumentNode, gql } from '@apollo/client';
import { faUserGroup } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage, FormattedNumber, defineMessages } from 'react-intl';

import { Link } from '@sorare/routing';

import { NavigatingButton } from '@sorare/core/src/atoms/buttons/NavigatingButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import {
  BodyM,
  BodyS,
  HeadlineXS,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import {
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_REWARDS,
  ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_PRIZE_POOL,
  ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_PRIZE_POOL_CONTENDER,
} from '@sorare/core/src/constants/routes';
import Warning from '@sorare/core/src/contexts/intl/Warning';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { DivisionChangeConfigBar } from 'components/leaderboard/DivisionChangeConfigBar';
import {
  RankRewards,
  Props as RankRewardsProps,
} from 'components/leaderboard/RankRewards';
import { ScoreRewards } from 'components/leaderboard/ScoreRewards';
import { ThresholdProgressBar } from 'components/leaderboard/ThresholdProgressBar';
import { ThresholdRewards } from 'components/leaderboard/ThresholdRewards';
import { isFixtureOpened } from 'lib/so5';

import {
  DefaultPrizePool_so5Leaderboard,
  DefaultPrizePool_so5LeaderboardContender,
} from './__generated__/index.graphql';

export const messages = defineMessages({
  leaderboardHasNoRewards: {
    id: 'leaderboard.DefaultPrizePool.leaderboardHasNoRewards',
    defaultMessage: 'This leaderboard has no rewards',
  },
});

export type Props = {
  hideTitle?: boolean;
  so5Leaderboard: DefaultPrizePool_so5Leaderboard;
  so5LeaderboardContender?: DefaultPrizePool_so5LeaderboardContender;
} & Pick<RankRewardsProps, 'onClickRanking'>;

export const DefaultPrizePool = ({
  hideTitle,
  so5Leaderboard,
  so5LeaderboardContender,
  onClickRanking,
}: Props) => {
  const { generateSportContextPath } = useSportContext();

  const hasDivisionChangeConfiguration =
    !!so5Leaderboard.divisionChangeConfiguration;
  const isCumulativeLeaderboard =
    !!so5Leaderboard.universalSo5UserGroups?.length;
  const isUpcoming =
    isCumulativeLeaderboard &&
    so5Leaderboard.universalSo5UserGroups[0].startSo5Fixture
      ? isFixtureOpened(
          so5Leaderboard.universalSo5UserGroups[0].startSo5Fixture
        )
      : isFixtureOpened(so5Leaderboard.so5Fixture);

  const hasRankRewards = !!(so5Leaderboard.rewardsConfig.ranking || []).length;
  const hasScoreRewards = !!(so5Leaderboard.rewardsConfig.conditional || [])
    .length;
  const hasTargetRewards = !!(so5Leaderboard.rewardsConfig.targetable || [])
    .length;

  const hasMultipleTypeRewards =
    [hasRankRewards, hasScoreRewards, hasTargetRewards].filter(Boolean).length >
    1;

  return (
    <Vertical gap={2}>
      {!hideTitle && (
        <SBHorizontal>
          <HeadlineXS as="h3">
            <FormattedMessage {...fantasy.leaderboard} />
          </HeadlineXS>
          <Horizontal>
            <FontAwesomeIcon icon={faUserGroup} size="xs" />
            <LabelS as="p" bold>
              <FormattedNumber value={so5Leaderboard.so5LineupsCount} />
            </LabelS>
          </Horizontal>
        </SBHorizontal>
      )}
      {hasTargetRewards && (
        <>
          {hasMultipleTypeRewards && (
            <BodyS bold>
              <FormattedMessage {...fantasy.rewardsPerTarget} />
            </BodyS>
          )}
          <ScoreRewards
            targetable
            defaultRarity={so5Leaderboard.mainRarityType}
            so5RewardConfigs={so5Leaderboard.rewardsConfig.targetable || []}
            so5League={so5Leaderboard.so5League}
            so5Ranking={so5LeaderboardContender?.so5Lineup?.so5Rankings[0]}
          />
        </>
      )}
      {hasScoreRewards && !hasTargetRewards && (
        <>
          {hasMultipleTypeRewards && (
            <BodyS bold>
              <FormattedMessage {...fantasy.rewardsPerThreshold} />
            </BodyS>
          )}
          <ThresholdRewards
            so5RewardConfigs={so5Leaderboard.rewardsConfig.conditional || []}
            defaultRarity={so5Leaderboard.mainRarityType}
            so5League={so5Leaderboard.so5League}
          />
          <ThresholdProgressBar
            so5Leaderboard={so5Leaderboard}
            so5Lineup={so5LeaderboardContender?.so5Lineup}
          />
        </>
      )}
      {hasRankRewards && (
        <>
          {hasMultipleTypeRewards && (
            <BodyS bold>
              <FormattedMessage {...fantasy.rewardsPerRank} />
            </BodyS>
          )}
          <RankRewards
            overview
            so5Leaderboard={so5Leaderboard}
            so5Ranking={so5LeaderboardContender?.so5Lineup?.so5Rankings[0]}
            onClickRanking={onClickRanking}
          />
        </>
      )}
      {!hasRankRewards && !hasScoreRewards && !hasTargetRewards && (
        <Warning title={messages.leaderboardHasNoRewards} variant="yellow" />
      )}
      {hasDivisionChangeConfiguration && (
        <DivisionChangeConfigBar
          so5Leaderboard={so5Leaderboard}
          so5Lineup={so5LeaderboardContender?.so5Lineup}
        />
      )}

      {!isUpcoming && (
        <NavigatingButton
          color="tertiary"
          size="medium"
          to={generateSportContextPath(
            so5LeaderboardContender
              ? ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_PRIZE_POOL_CONTENDER
              : ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_PRIZE_POOL,
            {
              params: {
                eventType: so5Leaderboard.so5Fixture.type.toLowerCase(),
                leaderboard: so5Leaderboard.slug,
                contender: so5LeaderboardContender?.slug || '',
              },
            }
          )}
        >
          <FormattedMessage {...fantasy.viewLeaderboard} />
        </NavigatingButton>
      )}
      {isUpcoming && (
        <Link
          to={generateSportContextPath(
            ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_REWARDS,
            {
              params: {
                eventType: so5Leaderboard.so5Fixture.type.toLowerCase(),
                fixture: so5Leaderboard.so5Fixture.slug,
              },
            }
          )}
        >
          <BodyM as="p" bold color="var(--c-brand-300)">
            <FormattedMessage {...fantasy.viewRewardsPool} />
          </BodyM>
        </Link>
      )}
    </Vertical>
  );
};

DefaultPrizePool.fragments = {
  so5Leaderboard: gql`
    fragment DefaultPrizePool_so5Leaderboard on So5Leaderboard {
      slug
      so5LineupsCount
      so5League {
        slug
        ...ScoreRewards_so5League
        ...ThresholdRewards_so5League
      }
      so5Fixture {
        slug
        type
        aasmState
      }
      divisionChangeConfiguration {
        id
        promotionRankBasisPoint
      }
      rewardsConfig {
        ranking {
          ranks
        }
        conditional {
          score
          ...ThresholdRewards_so5RewardConfig
        }
        targetable {
          score
          ...ScoreRewards_so5RewardConfig
        }
      }
      so5LeagueTrack {
        slug
        so5Leaderboards {
          slug
        }
      }
      universalSo5UserGroups {
        slug
        startSo5Fixture {
          slug
          ...isFixtureOpened_so5Fixture
        }
      }
      ...RankRewards_so5Leaderboard
      ...ThresholdProgressBar_so5Leaderboard
      ...DivisionChangeConfigBar_so5Leaderboard
    }
    ${ThresholdRewards.fragments.so5League}
    ${ThresholdRewards.fragments.so5RewardConfig}
    ${isFixtureOpened.fragments.so5Fixture}
    ${RankRewards.fragments.so5Leaderboard}
    ${ScoreRewards.fragments.so5League}
    ${ScoreRewards.fragments.so5RewardConfig}
    ${ThresholdProgressBar.fragments.so5Leaderboard}
    ${DivisionChangeConfigBar.fragments.so5Leaderboard}
  ` as TypedDocumentNode<DefaultPrizePool_so5Leaderboard>,
  so5LeaderboardContender: gql`
    fragment DefaultPrizePool_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Lineup {
        id
        so5Rankings {
          id
          ...RankRewards_so5Ranking
          ...ScoreRewards_so5Ranking
        }
        ...ThresholdProgressBar_so5Lineup
        ...DivisionChangeConfigBar_so5Lineup
      }
    }
    ${RankRewards.fragments.so5Ranking}
    ${ScoreRewards.fragments.so5Ranking}
    ${ThresholdProgressBar.fragments.so5Lineup}
    ${DivisionChangeConfigBar.fragments.so5Lineup}
  ` as TypedDocumentNode<DefaultPrizePool_so5LeaderboardContender>,
};
